const DEFAULT_TIMEOUT = 10000;
const responseCache = new Map();

export class ApiError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status ?? null;
    this.code = options.code ?? null;
    this.payload = options.payload;
  }
}

function attachAbortSignal(controller, signal) {
  if (!signal) {
    return () => {};
  }

  if (signal.aborted) {
    controller.abort();
    return () => {};
  }

  const abort = () => controller.abort();
  signal.addEventListener('abort', abort, { once: true });

  return () => signal.removeEventListener('abort', abort);
}

export async function fetchJson(url, options = {}) {
  const {
    timeout = DEFAULT_TIMEOUT,
    signal,
    headers,
    cacheTtl = 0,
    cacheKey = url,
    ...init
  } = options;
  const method = (init.method || 'GET').toUpperCase();
  const shouldUseCache = method === 'GET' && cacheTtl > 0;

  if (shouldUseCache) {
    const cached = responseCache.get(cacheKey);

    if (cached && cached.expiresAt > Date.now()) {
      return cached.payload;
    }

    responseCache.delete(cacheKey);
  }

  const controller = new AbortController();
  const detachAbort = attachAbortSignal(controller, signal);
  const timeoutId = window.setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      signal: controller.signal,
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw new ApiError(payload?.responseEnMsg || payload?.responseArMsg || 'Request failed.', {
        status: response.status,
        payload,
      });
    }

    if (shouldUseCache) {
      responseCache.set(cacheKey, {
        payload,
        expiresAt: Date.now() + cacheTtl,
      });
    }

    return payload;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new ApiError('The request took too long to finish.', { code: 'TIMEOUT' });
    }

    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError('Unable to reach the server. Please try again.', {
      payload: error,
    });
  } finally {
    detachAbort();
    window.clearTimeout(timeoutId);
  }
}

export function clearApiCache(cacheKey) {
  if (cacheKey) {
    responseCache.delete(cacheKey);
    return;
  }

  responseCache.clear();
}

export function getApiErrorMessage(error, fallbackMessage) {
  if (error instanceof ApiError && error.message) {
    return error.message;
  }

  if (error?.response?.data?.responseEnMsg) {
    return error.response.data.responseEnMsg;
  }

  if (error?.response?.data?.responseArMsg) {
    return error.response.data.responseArMsg;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  return fallbackMessage;
}

export function sanitizeRichText(html) {
  if (!html) {
    return '';
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll(
    'script, style, iframe, object, embed, form, input, base, math, svg'
  ).forEach((node) => {
    node.remove();
  });

  doc.querySelectorAll('details').forEach((node) => {
    const wrapper = doc.createElement('div');
    while (node.firstChild) {
      wrapper.appendChild(node.firstChild);
    }
    node.replaceWith(wrapper);
  });

  const scriptProtocol = ['java', 'script:'].join('');

  doc.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();

      if (name.startsWith('on')) {
        element.removeAttribute(attribute.name);
        return;
      }

      if ((name === 'href' || name === 'src' || name === 'action' || name === 'formaction' || name === 'xlink:href') && value.replace(/[\s\x00-\x1f]+/g, '').startsWith(scriptProtocol)) {
        element.removeAttribute(attribute.name);
        return;
      }

      if (name === 'style') {
        const normalized = value.replace(/[\s\x00-\x1f]+/g, '').toLowerCase();
        if (normalized.includes('expression(') || normalized.includes(scriptProtocol)) {
          element.removeAttribute(attribute.name);
        }
      }
    });
  });

  return doc.body.innerHTML;
}
